import type { ReactNode } from 'react';
import { useLocation } from 'wouter';
import { useIsDesktop, useIsXLDesktop } from '@/hooks/use-device-context';
import { DesktopSidebar } from './desktop-sidebar';
import { DesktopTopBar, type DesktopTopBarAction } from './desktop-top-bar';
import { DesktopChatRail } from './desktop-chat-rail';
import { KG, DESKTOP_NAV } from './desktop-tokens';
import { DetailPanelHost } from './detail-panel-host';

interface DesktopShellProps {
  children: ReactNode;
  /** Override the auto-derived title for the current route. */
  title?: string;
  subtitle?: string;
  actions?: DesktopTopBarAction[];
  showSearch?: boolean;
  unreadChatCount?: number;
  /** Force-hide the right chat rail (e.g. on the dedicated /chat page). */
  hideChatRail?: boolean;
}

const ROUTE_TITLES: Record<string, { title: string; subtitle?: string }> = {
  '/': { title: 'Scopri' },
  '/discover': { title: 'Scopri' },
  '/wall': { title: 'Wall' },
  '/events': { title: 'Eventi' },
  '/groups': { title: 'Gruppi' },
  '/chat': { title: 'Chat' },
  '/speed': { title: 'Speed dates' },
  '/organizer': { title: 'Organizer' },
  '/admin': { title: 'Admin' },
};

function deriveTitle(location: string): { title: string; subtitle?: string } {
  if (ROUTE_TITLES[location]) return ROUTE_TITLES[location];
  const match = DESKTOP_NAV.find((n) => n.matches(location));
  if (match) return { title: match.label };
  return { title: 'Kinkygram' };
}

export function DesktopShell({
  children,
  title,
  subtitle,
  actions,
  showSearch = true,
  unreadChatCount = 0,
  hideChatRail = false,
}: DesktopShellProps) {
  const [location] = useLocation();
  const isDesktop = useIsDesktop();
  const sidebarCollapsed = !isDesktop;
  // Chat rail dal lg in su (≥1024px) — handoff-detail-panel: 380/300/260
  // Nascondi sulla pagina chat dedicata per non duplicare la lista.
  const showChatRail = isDesktop && !hideChatRail && !location.startsWith('/chat');

  const derived = deriveTitle(location);
  const finalTitle = title ?? derived.title;
  const finalSubtitle = subtitle ?? derived.subtitle;

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        background: KG.bg,
        color: KG.text,
      }}
    >
      <DesktopSidebar collapsed={sidebarCollapsed} unreadChatCount={unreadChatCount} />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DesktopTopBar
          title={finalTitle}
          subtitle={finalSubtitle}
          actions={actions}
          showSearch={showSearch}
        />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', position: 'relative' }}>
          <main style={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden' }}>
            {children}
          </main>
          {showChatRail && <DesktopChatRail />}
          <DetailPanelHost />
        </div>
      </div>
    </div>
  );
}

export default DesktopShell;
